import { useCallback, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '~/components/general/Button'
import { FlexContainer, FlexItem } from '~/components/Grid'
import { ArrowCardsIcon, ManRunIcon, MenuDotsIcon, PlayArrowWhiteIcon } from '~/components/icons'
import { trackEvent } from '~/utils/tracking'

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { CustomEase } from 'gsap/CustomEase'
import { VideoPlayer } from '~/components/VideoPlayer'
import { TypeHomeHeroBlock } from '~/types/contentful'

gsap.registerPlugin(ScrollTrigger, CustomEase)

const bookmarks = [
  {
    height: '197px',
    backgroundImage: '/images/Service_Card.webp',
    backgroundColor: ' #f4f6fa',
  },
  {
    height: '287px',
    backgroundImage: '/images/Service_Card2.webp',
    backgroundColor: '#f4f6fa',
  },
  {
    height: '325px',
    backgroundImage: '/images/Service_Card3.webp',
    backgroundColor: ' #f4f6fa',
  },
  {
    height: '307px',
    backgroundImage: '/images/Service_Card4.webp',
    backgroundColor: ' #f4f6fa',
  },
  {
    height: '264px',
    backgroundImage: '/images/Service_Card5.webp',
    backgroundColor: '#f4f6fa',
  },
]

type HeroProps = {
  section: TypeHomeHeroBlock<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

export const Hero = ({ section }: HeroProps) => {
  const data = section.fields

  const description = data.description?.content[0].content.reduce((acc: string, item) => {
    if (item.nodeType === 'text' && item.marks[0]?.type === 'bold') {
      acc += `<span>${item.value}</span>`
    } else if (item.nodeType === 'text') {
      acc += item.value
    }

    return acc
  }, '')

  const [Video, setVideo] = useState(
    'https://www.dropbox.com/s/q7v5447ul32hkqp/CIO%202%20SFX.mp4?raw=1',
  )
  const [videoModelStatus, setVideoModelStatus] = useState(false)
  const close = useCallback(() => setVideoModelStatus(false), [])
  useEffect(() => {
    const ctx = gsap.context(() => {
      const easing = CustomEase.create('custom', 'M0,0,C0.104,0.204,0.032,1.162,1,1')
      gsap.config({
        autoSleep: 60,
        force3D: true,
      })
      const tl = gsap.timeline({
        delay: 0.2,
      })

      tl.to('.animate-wrapper', 1, {
        y: 0,
      })
        .to(
          '.animated-man',
          1.3,
          {
            autoAlpha: 1,
            y: 0,
          },
          '-=1.4',
        )
        .to(
          '.animated-arrow',
          0.8,
          {
            y: 0,
            autoAlpha: 1,
          },
          '-=1',
        )
        .to(
          '.animated-card:nth-child(even)',
          1.4,
          {
            y: 0,
            ease: easing,
          },
          '-=1.2',
        )
        .to(
          '.animated-card:nth-child(odd)',
          1.4,
          {
            y: 0,
            ease: easing,
          },
          '-=1.3',
        )
    })

    setVideo(data.videoUrl ?? '')
    return () => ctx.revert()
  }, [data.videoUrl])

  const handleCTABtnClick = () => {
    trackEvent('homepage_click_cta', {
      block: data.blockId as string,
      target: data.secondButtonLink ?? '',
      homepage_CTA: data.secondButtonName ?? '',
    })
  }

  const handleVideoPlayBtnClick = () => {
    setVideoModelStatus(!videoModelStatus)
    trackEvent('homepage_watch_video')
  }

  return (
    <>
      <section
        className="hero-container flex flex-col bg-neutral-5 rounded-[40px] overflow-hidden gap-6"
        data-block-id={data.blockId}
      >
        <FlexContainer className="max-xl:max-w-[1010px]">
          <FlexContainer
            className="max-md:gap-3 max-md:mb-10 relative mb-12 max-xl:mb-14 max-lg:gap-6 max-lg:items-center max-lg:flex-col"
            justify="between"
            flexWrap="nowrap"
          >
            <FlexItem className="flex flex-col max-md:text-center" desktop={8} tablet={9}>
              <h1 className="text-2xl mb-5 text-neutral-100 max-xl:mb-7 max-md:mb-0 max-lg:text-center">
                {data.title}
              </h1>
              <div className="flex gap-4 max-lg:hidden text-s">
                {data.blockId === 'home_hero_with_video' && (
                  <Button
                    iconLeft={<PlayArrowWhiteIcon />}
                    type={'link'}
                    text={data.videoButtonName}
                    className={'inline-flex gap-3'}
                    onClick={handleVideoPlayBtnClick}
                    href="#"
                    size={'m'}
                  />
                )}
                {data.blockId === 'home_hero_with_video' && (
                  <Button
                    text={data.secondButtonName}
                    type={'link'}
                    href={data.secondButtonLink}
                    color={'lightBlue'}
                    onClick={handleCTABtnClick}
                    className={'!hidden lg:!inline-flex'}
                    size={'m'}
                  />
                )}
              </div>
            </FlexItem>
            <FlexItem
              desktop={4}
              laptop={4}
              tablet={9}
              className="flex flex-col text-right max-lg:text-center"
            >
              <p
                className="mb-10 mt-11 text-neutral-60 [&_span]:text-neutral-90 max-xl:mb-9 max-lg:mt-0 max-lg:mb-6 max-md:hidden text-s text-left"
                dangerouslySetInnerHTML={{ __html: description as string }}
              />
            </FlexItem>

            <div className="hidden gap-4 max-lg:flex text-s flex-col items-center text-center">
              {data.blockId === 'home_hero_with_video' && (
                <Button
                  className={'!inline-flex !gap-3'}
                  type={'link'}
                  onClick={handleVideoPlayBtnClick}
                  href="#"
                  iconLeft={<PlayArrowWhiteIcon />}
                  text={data.videoButtonName}
                />
              )}
              {data.blockId === 'home_hero_with_video' && (
                <Button
                  type={'link'}
                  text={data.secondButtonName}
                  href={data.secondButtonLink}
                  color={'lightBlue'}
                  onClick={handleCTABtnClick}
                  className={'lg:!inline-flex'}
                />
              )}
            </div>
          </FlexContainer>
          <FlexContainer
            style={{
              position: 'relative',
            }}
          >
            <div
              className={
                'max-xl:left-[24.2vw] max-xl:bottom-[29.7vw] max-lg:left-[19.2vw] max-lg:bottom-[50.7vw] max-md:bottom-[25.7vw] max-md:left-[21vw] max-md:[&_svg]:w-16 absolute left-[54.4%] bottom-[68.4%] opacity-0 z-2 animated-man'
              }
              style={{ willChange: 'transform', transform: 'translateY(150px)' }}
            >
              <ManRunIcon />
            </div>
            <div
              className="max-md:right-[23vw] max-md:top-[1vw] max-md:[&_svg]:w-[74px] right-[2.5%] top-[-17%] max-xl:right-[28.5vw] max-xl:top-[1.5vw] max-lg:right-[19.5vw] max-lg:top-[1.5vw] absolute opacity-0 translate-y-25 z-2 animated-arrow"
              style={{ willChange: 'transform' }}
            >
              <ArrowCardsIcon />
            </div>
            <div
              className="bg-neutral-10 absolute left-0 bottom-0 right-0 h-[74%] max-md:hidden max-xl:-left-9 max-xl:-right-9"
              style={{ borderRadius: '40px 40px 0 0' }}
            />

            <div
              className="animate-wrapper max-md:h-[267px] max-md:w-full max-md:rounded-[14px] max-md:pt-4 max-md:px-5 relative left-1/2 top-0 bg-white w-[80%] rounded-[32px_32px_0_0] flex-col flex h-[420px] p-11 pb-0 max-xl:w-[83%] max-xl:h-[500px] max-xl:px-7 max-xl:pt-6"
              style={{
                willChange: 'transform',
                boxShadow:
                  '0px 1px 2px 0px rgba(15, 56, 113, 0.12), 0px 0px 1px 0px rgba(15, 56, 113, 0.32)',
                transform: 'translate(-50%, 0%)',
              }}
            >
              <div className="flex items-center z-2">
                <span>
                  <MenuDotsIcon
                    className="max-md:w-3 max-md:h-3"
                    style={{ marginRight: '45px', display: 'flex' }}
                  />
                </span>
                <FlexContainer className="flex gap-6 max-xl:!hidden">
                  <span style={{ color: '#1A91F0', fontSize: '16px' }} className={'animated-find'}>
                    Find a New Job
                  </span>
                  <span style={{ color: '#D9DEEB', fontSize: '16px' }}>Excel at Your Job</span>
                  <span style={{ color: '#D9DEEB', fontSize: '16px' }}>Change Career</span>
                </FlexContainer>
              </div>
              <FlexContainer
                className="max-xl:-translate-x-1/2 max-md:w-[105%] mt-auto max-xl:w-[130%] max-xl:relative max-xl:left-1/2"
                justify={'between'}
                flexWrap={'nowrap'}
              >
                <FlexContainer
                  justify={'between'}
                  flexWrap={'nowrap'}
                  style={{
                    bottom: 0,
                    gap: '8px',
                    marginBottom: '-7px',
                    alignItems: 'flex-start',
                  }}
                >
                  {bookmarks.map((bookmark, idx) => (
                    <FlexContainer
                      className={
                        'max-lg:[&>div]:text-center max-lg:gap-8 max-lg:items-center max-lg:flex-col-reverse max-lg:[&>div]:flex-col max-lg:[&>div]:flex max-lg:[&>div]:items-center animated-card relative flex-col max-w-full [&>img]:rounded-lg translate-y-100'
                      }
                      style={{ willChange: 'transform' }}
                      justify={'between'}
                      key={idx}
                    >
                      <img
                        src={bookmark.backgroundImage}
                        style={{ width: '100%' }}
                        alt="Career platform Career.io"
                      />
                    </FlexContainer>
                  ))}
                </FlexContainer>
              </FlexContainer>
            </div>
          </FlexContainer>
        </FlexContainer>
        <p
          className="text-s hidden mb-10 mt-11 py-0 px-4 text-neutral-60 max-xl:mb-9 [&_span]:text-neutral-90 max-md:block max-md:mb-3 max-md:mt-5"
          dangerouslySetInnerHTML={{ __html: description as string }}
        />
      </section>

      {videoModelStatus &&
        typeof document !== 'undefined' &&
        createPortal(<VideoPlayer videoUrl={Video} onClose={close} />, window.document.body)}
    </>
  )
}
